/* eslint-disable max-lines */
import {
    useEffect,
 useState
} from 'react';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Button
} from 'shared/components/ui/button';
import {
    ChevronDown,
    ChevronRight,
    Clock,
    XCircle,
    CheckCircle,
    Activity,
    Bug,
    X,
    Copy,
    Check,
    UnfoldHorizontal,
    WrapText
} from 'lucide-react';
import {
    CPIEResponse
} from 'builders/builderOpcode/types/newOpcodeTypes.types';

export function ShowExecuteResponse({
 response
}: { response: CPIEResponse }) {
    if (!response) {
        return null;
    }
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        metadata: true,
        outputs: true,
        warnings: true,
        errors: true,
        debug: false,
        streaming: true
    });
     const [debugPanelOpen, setDebugPanelOpen] = useState<boolean>(false);

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'ok':
                return <CheckCircle className="h-4 w-4" />;
            case 'running':
                return <Activity className="h-4 w-4" />;
            case 'error':
                return <XCircle className="h-4 w-4" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ok':
                return 'bg-success/10 text-success border-success/20';
            case 'running':
                return 'text-accent border-accent/20';
            case 'error':
                return 'bg-destructive/10 text-destructive border-destructive/20';
            default:
                return 'bg-muted text-muted-foreground';
        }
    };

    return (
        <div className="flex gap-4 w-full">
            <div className="flex-1 space-y-4 w-full overflow-auto">
                {response.debug && (
                    <div className="flex justify-end">
                        <Button onClick={() => setDebugPanelOpen(!debugPanelOpen)} variant='outline' className='gap-2'>
                            <Bug className="h-4 w-4" />
                            {debugPanelOpen ? 'Hide Debug' : 'Show Debug'}
                        </Button>
                    </div>
                )}
                {/* Metadata Section */}
                <Card className="border-border bg-card">
                    <CardHeader className="cursor-pointer" onClick={() => toggleSection('metadata')}>
                        <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                {expandedSections.metadata ? (
                                    <ChevronDown className="h-5 w-5" />
                                ) : (
                                    <ChevronRight className="h-5 w-5" />
                                )}
                                Request Metadata
                            </CardTitle>
                            <Badge
                                variant="default"
                                className={`flex items-center gap-1.5 ${getStatusColor(response.status)}`}
                            >
                                {getStatusIcon(response.status)}
                                {response.status.toUpperCase()}
                            </Badge>
                        </div>
                    </CardHeader>
                    {expandedSections.metadata && (
                        <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Request ID</p>
                                    <code className="block rounded bg-secondary px-3 py-2 font-mono text-sm text-foreground">
                                        {response.request_id}
                                    </code>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Duration</p>
                                    <div className="flex items-center gap-2 rounded bg-secondary px-3 py-2">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-mono text-sm text-foreground">
                                            {response.timing.duration_ms}ms
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-muted-foreground">Opcode</p>
                                <div className="rounded bg-secondary p-3">
                                    <div className="grid gap-2 font-mono text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">ID:</span>
                                            <span className="text-foreground">{response.opcode.id}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Type:</span>
                                            <Badge variant="outline" className="font-mono">
                                                {response.opcode.type}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Version:</span>
                                            <span className="text-foreground">{response.opcode.version}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-muted-foreground">Started At</p>
                                <code className="block rounded bg-secondary px-3 py-2 font-mono text-sm text-foreground">
                                    {new Date(response.timing.started_at).toLocaleString()}
                                </code>
                            </div>
                        </CardContent>
                    )}
                </Card>

                {/* Outputs Section */}
                <Card className="border-border bg-card">
                    <CardHeader className="cursor-pointer" onClick={() => toggleSection('outputs')}>
                        <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                {expandedSections.outputs ? (
                                    <ChevronDown className="h-5 w-5" />
                                ) : (
                                    <ChevronRight className="h-5 w-5" />
                                )}
                                Outputs
                                <Badge variant="secondary" className="ml-2">
                                    {response.outputs.length}
                                </Badge>
                            </CardTitle>
                        </div>
                    </CardHeader>
                    {expandedSections.outputs && (
                        <CardContent className="space-y-3">
                            {response.outputs.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No outputs available</p>
                            ) : (
                                response.outputs.map((output, idx) => (
                                    <OutputItem key={idx} output={output} />
                                ))
                            )}
                        </CardContent>
                    )}
                </Card>

                {/* Errors Section */}
                {response.errors.length > 0 && (
                    <Card className="border-destructive/20 bg-card">
                        <CardHeader className="cursor-pointer" onClick={() => toggleSection('errors')}>
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2 text-lg text-destructive">
                                    {expandedSections.errors ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                                    <XCircle className="h-5 w-5" />
                                    Errors
                                    <Badge variant="outline" className="border-destructive/20 bg-destructive/10 text-destructive">
                                        {response.errors.length}
                                    </Badge>
                                </CardTitle>
                            </div>
                        </CardHeader>
                        {expandedSections.errors && (
                            <CardContent className="space-y-3">
                                {response.errors.map((error, idx) => (
                                    <ErrorItem key={idx} error={error} />
                                ))}
                            </CardContent>
                        )}
                    </Card>
                )}
            </div>
            {/* Debug Section */}
            {response.debug && debugPanelOpen && (
                <DebugSideBar
                    response={response}
                    setDebugPanelOpen={setDebugPanelOpen}
                />
            )}
        </div>
    );
}

function DebugSideBar({
    response, setDebugPanelOpen
}: {
    response: CPIEResponse,
    setDebugPanelOpen: (open: boolean) => void
}) {

    return (
        <div className="w-full md:w-[500px] border border-border rounded-lg bg-card shadow-lg overflow-hidden flex flex-col shrink-0">
            <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-4 py-3">
                <div className="flex items-center gap-2">
                    <Bug className="h-5 w-5 text-foreground" />
                    <h3 className="font-semibold text-foreground">Debug Information</h3>
                    <Badge className='' variant="secondary">Advanced</Badge>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setDebugPanelOpen(false)} className="h-8 w-8">
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[800px]">
                <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">Run Metadata</p>
                    <DisplayCodeBlock data={response.debug.run} />
                </div>

                <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Step Traces ({response.debug.steps.length})</p>
                    {response.debug.steps.map((step, idx) => (
                        <DebugStepItem key={idx} step={step} index={idx} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function OutputItem({
    output
}: { output: any }) {
    const [expanded, setExpanded] = useState<boolean>(false);

    return (
        <div className="rounded border border-border bg-secondary/50 p-3">
            <div className="mb-2 flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                            {output.step_type}
                        </Badge>
                        <span className="font-mono text-sm font-medium text-foreground">{output.label}</span>
                        {output.truncated && (
                            <Badge variant="outline" className="border-warning/20 bg-warning/10 text-warning text-xs">
                                Truncated
                            </Badge>
                        )}
                    </div>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">Step: {output.step_id}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-8 w-8 p-0">
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
            </div>

            {expanded && (
                <DisplayCodeBlock data={output.data} />
            )}
        </div>
    );
}

function ErrorItem({
 error
}: { error: any }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="rounded border border-destructive/20 bg-destructive/5 p-3">
            <div className="mb-2 flex items-start justify-between">
                <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                        <Badge
                            variant="outline"
                            className="border-destructive/20 bg-destructive/10 text-destructive font-mono text-xs"
                            >
                            {error.code}
                        </Badge>
                        <Badge variant="outline" className="font-mono text-xs">
                            {error.type}
                        </Badge>
                        {error.retryable && (
                            <Badge variant="outline" className="border-success/20 bg-success/10 text-success text-xs">
                                Retryable
                            </Badge>
                        )}
                    </div>
                    <p className="text-sm text-foreground">{error.message}</p>
                    <p className="font-mono text-xs text-muted-foreground">{new Date(error.time).toLocaleString()}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-8 w-8 p-0">
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
            </div>

            {expanded && (
                <div className="mt-3 space-y-2">
                    {error.step && (
                        <div className="rounded bg-background p-2">
                            <p className="mb-1 text-xs font-medium text-muted-foreground">Step Context</p>
                            <div className="font-mono text-xs text-foreground">
                                <div>ID: {error.step.id}</div>
                                <div>Type: {error.step.type}</div>
                                <div>Index: {error.step.index}</div>
                            </div>
                        </div>
                    )}
                    {error.details && (
                        <div className="rounded bg-background p-2">
                            <p className="mb-1 text-xs font-medium text-muted-foreground">Error Details</p>
                            <DisplayCodeBlock data={error.details} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function DebugStepItem({
    step,
    index
}: { step: any; index: number }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="rounded border border-border bg-background p-3">
            <div className="mb-2 flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                            #{index}
                        </Badge>
                        <Badge variant="outline" className="font-mono text-xs">
                            {step.type}
                        </Badge>
                        <span className="font-mono text-sm text-foreground">{step.id}</span>
                    </div>
                    {step.elapsed_ms !== undefined && (
                        <p className="mt-1 font-mono text-xs text-muted-foreground">Duration: {step.elapsed_ms}ms</p>
                    )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-8 w-8 p-0">
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
            </div>

            {expanded && (
                <DisplayCodeBlock data={step} />
            )}
        </div>
    );
}

export function DisplayCodeBlock({
    data
}: { data: any }) {
    const [copied, setCopied] = useState<boolean>(false);
    const [wrapText, setWrapText] = useState<boolean>(true);
    const [outputData, setOutputData] = useState<string>('');

    const handleCopy = () => {
        navigator.clipboard.writeText(JSON.stringify(data, null, 4));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    useEffect(() => {
        setOutputData(displayOutput(data));
    }, [data]);

    function displayOutput(data: any): string {
        let outputData = data;
        if (typeof data === 'string') {
            try {
                outputData = JSON.parse(data);
            } catch (error) {
                console.error('Error parsing output data:', error);
                return data;
            }
        }

        if (typeof outputData === 'object') {
            return JSON.stringify(outputData, null, 4);
        }

        return '';
    }

    return (
        <div className="mt-2 rounded border border-border bg-background overflow-hidden">
            <div className="flex items-center justify-between bg-secondary/50 px-3 py-2 border-b border-border">
                <span className="text-xs font-medium text-muted-foreground">Output Data</span>
                {outputData && (
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setWrapText(!wrapText)} className="gap-2">
                            {wrapText ? (
                                <>
                                    <UnfoldHorizontal className="h-4 w-4" />
                                    Scroll
                                </>
                            ) : (
                                <>
                                    <WrapText className="h-4 w-4" />
                                    Wrap
                                </>
                            )}
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2 bg-transparent">
                            {copied ? (
                                <>
                                    <Check className="h-4 w-4" />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4" />
                                    Copy
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
            <div
            className={`${wrapText ? 'overflow-auto' : 'overflow-x-auto'} bg-muted rounded-md border border-border`}>
                <pre
                className={`p-4 text-sm font-mono ${wrapText ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'}`}
                >
                    <code>
                        {outputData}
                    </code>
                </pre>
            </div>
        </div>
    );
}